import React, { useState, useEffect, useMemo } from 'react';
import { Spinner } from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';
import axios from 'axios';

/* ── ícone reutilizável ── */
const Icon = ({ d, size = 16, color = 'currentColor', stroke = 2, fill = 'none' }) => (
  <svg width={size} height={size} fill={fill} stroke={color} viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={stroke} d={d} />
  </svg>
);

const ICONS = {
  info:    'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  up:      'M5 10l7-7m0 0l7 7m-7-7v18',
  down:    'M19 14l-7 7m0 0l-7-7m7 7V3',
  chart:   'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  calendar:'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  warn:    'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
};

/* ── tooltip nativo ── */
const Tip = ({ text, children }) => {
  const [show, setShow] = React.useState(false);
  return (
    <span
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', cursor: 'help' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && text && (
        <span style={{
          position: 'absolute', bottom: '130%', left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#1e293b', color: '#fff', fontSize: `calc(11px * var(--font-scale))`,
          padding: '7px 11px', borderRadius: '7px', zIndex: 9999,
          boxShadow: '0 4px 16px rgba(0,0,0,0.25)', pointerEvents: 'none',
          maxWidth: '260px', whiteSpace: 'normal', textAlign: 'center', lineHeight: '1.5',
          minWidth: '160px',
        }}>
          {text}
          <span style={{
            position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
            borderWidth: '5px', borderStyle: 'solid',
            borderColor: '#1e293b transparent transparent transparent',
          }} />
        </span>
      )}
    </span>
  );
};

/* ── mapeamento de tooltips ── */
const TOOLTIPS = {
  faturamento_hoje:                 'Soma de todos os pagamentos confirmados (pacientes e convênios) no dia de hoje.',
  faturamento_bruto:                'Soma de todos os pagamentos confirmados (pacientes e convênios) no período selecionado.',
  lucro_bruto:                      'Faturamento menos os custos diretos (Despesas Variáveis).',
  margem_contribuicao:              'Valor que sobra do Faturamento Bruto para pagar despesas fixas (Faturamento - Custos e Deduções).',
  ebitda:                           'Lucro antes de juros, impostos, depreciação e amortização (Faturamento - Despesas Fixas e Variáveis).',
  resultado_liquido:                'Lucro final após todas as despesas e impostos (Ebitda - Impostos e Desp. não Operacionais).',
  liquido_anual:                    'Soma do Resultado Líquido dos últimos 12 meses. Mostra o lucro acumulado no ano.',
  despesas_variaveis:               'Gastos que variam conforme o volume de atendimentos, como comissões e materiais diretos.',
  despesas_fixas:                   'Gastos recorrentes que não mudam com o volume de atendimentos, como aluguel e salários.',
  custos_e_deducoes:                'Custos diretos ligados à prestação do serviço e deduções sobre o faturamento (ex: ISS).',
  impostos_desp_nao_operacionais:   'Impostos sobre o lucro (IRPJ, CSLL) e outras despesas não ligadas à operação principal.',
};

/* ════════════ MetricCard ════════════ */
const MetricCard = ({ title, value, percentage, formatCurrency, tooltipKey }) => {
  const isPositive = percentage >= 0;
  return (
    <div style={{
      backgroundColor: '#fff', borderRadius: '12px',
      border: '1px solid #e5e7eb', padding: '16px',
      display: 'flex', flexDirection: 'column', gap: '6px', height: '100%',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {title}
        </span>
        <Tip text={TOOLTIPS[tooltipKey]}>
          <Icon d={ICONS.info} size={13} color="#9ca3af" />
        </Tip>
      </div>
      <p style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827', lineHeight: 1.2 }}>
        {formatCurrency(value ?? 0)}
      </p>
      {percentage !== null && typeof percentage === 'number' && (
        <span style={{
          fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700',
          color: isPositive ? '#16a34a' : '#dc2626',
          display: 'flex', alignItems: 'center', gap: '3px',
        }}>
          <Icon d={isPositive ? ICONS.up : ICONS.down} size={10} color={isPositive ? '#16a34a' : '#dc2626'} stroke={2.5} />
          {percentage.toFixed(2)}%
        </span>
      )}
    </div>
  );
};

/* ════════════ DetailedMetricCard ════════════ */
const DetailedMetricCard = ({ title, data, formatCurrency, tooltipKey }) => {
  const isObj = typeof data === 'object' && data !== null;
  const valor        = isObj ? data.valor        : data;
  const percentual_mes = isObj ? data.percentual_mes : null;
  const percentual_ano = isObj ? data.percentual_ano : null;
  const isPositive   = (percentual_mes ?? 0) >= 0;

  return (
    <div style={{
      backgroundColor: '#fff', borderRadius: '12px',
      border: '1px solid #e5e7eb', padding: '16px',
      display: 'flex', flexDirection: 'column', gap: '6px', height: '100%',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: '1.3' }}>
          {title}
        </span>
        <Tip text={TOOLTIPS[tooltipKey]}>
          <Icon d={ICONS.info} size={13} color="#9ca3af" />
        </Tip>
      </div>
      <p style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111827' }}>
        {formatCurrency(valor ?? 0)}
      </p>
      {isObj && percentual_mes !== null && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
          <span style={{
            fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700',
            color: isPositive ? '#2563eb' : '#dc2626',
            display: 'flex', alignItems: 'center', gap: '3px',
          }}>
            <Icon d={isPositive ? ICONS.up : ICONS.down} size={10} color={isPositive ? '#2563eb' : '#dc2626'} stroke={2.5} />
            {percentual_mes?.toFixed(2)}% Mês
          </span>
          <span style={{ fontSize: `calc(11px * var(--font-scale))`, color: '#9ca3af' }}>
            {percentual_ano?.toFixed(2)}% Ano
          </span>
        </div>
      )}
    </div>
  );
};

/* ════════════ ChartCard ════════════ */
const ChartCard = ({ title, data, options, height = 140 }) => (
  <div style={{
    backgroundColor: '#fff', borderRadius: '12px',
    border: '1px solid #e5e7eb', padding: '16px',
  }}>
    <p style={{ margin: '0 0 10px', fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
      {title}
    </p>
    <div style={{ height }}>
      <Bar data={data} options={options} />
    </div>
  </div>
);

/* ════════════════════════════════════════════════════════ */

const DashboardFinanceiroTab = ({ isActive, currentEntidade, formatCurrency }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading]         = useState(false);
  const [error, setError]                 = useState(null);

  const [filterDate, setFilterDate] = useState(() => {
    const now   = new Date();
    const year  = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  });

  useEffect(() => {
    if (!isActive || !currentEntidade || !filterDate) return;
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await axios.get('/api/dashboard/financeiro/completo', {
          params: { entidade_id: currentEntidade.id, periodo: filterDate },
        });
        setDashboardData(response.data);
      } catch (err) {
        console.error('Erro ao buscar dados do dashboard financeiro:', err);
        setError('Não foi possível carregar os dados do dashboard.');
        setDashboardData(null);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [isActive, currentEntidade, filterDate]);

  const truncateLabel = (label, max = 20) =>
    label.length > max ? label.substring(0, max) + '…' : label;

  const chartOptions = (title) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      title:  { display: false },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { callback: v => formatCurrency(v), font: { size: 10 } },
        grid:  { color: '#f1f5f9' },
      },
      x: { ticks: { font: { size: 10 } }, grid: { display: false } },
    },
  });

  const faturamentoCategoriaChart = useMemo(() => {
    const d = dashboardData?.faturamento_por_categoria;
    if (!d) return { labels: [], datasets: [] };
    return {
      labels:   d.labels.map(l => truncateLabel(l)),
      datasets: [{ label: 'Faturamento', data: d.values, backgroundColor: 'rgba(37,99,235,0.6)', borderRadius: 4 }],
    };
  }, [dashboardData]);

  const faturamentoDiarioChart = useMemo(() => {
    const d = dashboardData?.faturamento_diario_mes;
    if (!d?.labels?.length) return { labels: [], datasets: [] };
    return {
      labels:   d.labels,
      datasets: [{ label: 'Faturamento Diário', data: d.values, backgroundColor: 'rgba(139,92,246,0.6)', borderRadius: 4 }],
    };
  }, [dashboardData]);

  const historicoFaturamentoChart = useMemo(() => {
    const d = dashboardData?.historico_faturamento_bruto;
    if (!d) return { labels: [], datasets: [] };
    return {
      labels:   d.labels,
      datasets: [{ label: 'Faturamento Bruto', data: d.values, backgroundColor: 'rgba(20,184,166,0.6)', borderRadius: 4 }],
    };
  }, [dashboardData]);

  const despesasCategoriaChart = useMemo(() => {
    const d = dashboardData?.despesas_por_categoria;
    if (!d) return { labels: [], datasets: [] };
    return {
      labels:   d.labels.map(l => truncateLabel(l)),
      datasets: [{ label: 'Despesas', data: d.values, backgroundColor: 'rgba(239,68,68,0.6)', borderRadius: 4 }],
    };
  }, [dashboardData]);

  /* ── estilos compartilhados ── */
  const inputStyle = {
    padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb',
    fontSize: `calc(13px * var(--font-scale))`, color: '#374151', outline: 'none',
    fontFamily: "'Inter','Segoe UI',sans-serif", backgroundColor: '#fff',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  };

  /* ── FILTRO DE PERÍODO ── */
  const PeriodFilter = () => (
    <div style={{
      backgroundColor: '#fff', borderRadius: '12px',
      border: '1px solid #e5e7eb', padding: '14px 20px',
      marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px',
    }}>
      <Icon d={ICONS.calendar} size={15} color="#2563eb" />
      <span style={{ fontSize: `calc(12px * var(--font-scale))`, fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        Período
      </span>
      <input
        type="month"
        value={filterDate}
        onChange={e => setFilterDate(e.target.value)}
        style={inputStyle}
        onFocus={e => { e.target.style.borderColor = '#2563eb'; e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.1)'; }}
        onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
      />
    </div>
  );

  /* ── ESTADOS DE CARREGAMENTO / ERRO / VAZIO ── */
  if (isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px', gap: '14px' }}>
        <Spinner animation="border" variant="primary" style={{ width: '36px', height: '36px' }} />
        <p style={{ margin: 0, fontSize: `calc(13px * var(--font-scale))`, color: '#6b7280' }}>Carregando dashboard financeiro...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '20px 24px', borderRadius: '12px', marginBottom: '16px',
        backgroundColor: '#fef2f2', border: '1px solid #fecaca',
        display: 'flex', alignItems: 'center', gap: '10px',
      }}>
        <Icon d={ICONS.warn} size={16} color="#dc2626" />
        <span style={{ fontSize: `calc(13px * var(--font-scale))`, color: '#dc2626' }}>{error}</span>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <>
        <PeriodFilter />
        <div style={{
          backgroundColor: '#fff', borderRadius: '12px',
          border: '1px dashed #e5e7eb', padding: '60px 24px', textAlign: 'center',
        }}>
          <Icon d={ICONS.chart} size={36} color="#d1d5db" />
          <p style={{ margin: '12px 0 0', fontSize: `calc(14px * var(--font-scale))`, fontWeight: '600', color: '#374151' }}>
            Nenhum dado disponível para o período selecionado.
          </p>
        </div>
      </>
    );
  }

  /* ── RENDER PRINCIPAL ── */
  return (
    <div style={{ fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');`}</style>

      <PeriodFilter />

      {/* ── Linha 1: KPIs principais ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        {[
          { title: 'Faturamento Bruto',  key: 'faturamento_bruto',   pct: null },
          { title: 'Lucro Bruto',        key: 'lucro_bruto',         pct: dashboardData.lucro_bruto?.percentual },
          { title: 'Margem Contrib.',    key: 'margem_contribuicao', pct: dashboardData.margem_contribuicao?.percentual },
          { title: 'EBITDA',             key: 'ebitda',              pct: dashboardData.ebitda?.percentual },
          { title: 'Resultado Líquido',  key: 'resultado_liquido',   pct: dashboardData.resultado_liquido?.percentual },
          { title: 'Líquido Anual',      key: 'liquido_anual',       pct: dashboardData.liquido_anual?.percentual },
        ].map(item => (
          <MetricCard
            key={item.key}
            title={item.title}
            value={dashboardData[item.key]?.valor}
            percentage={item.pct}
            formatCurrency={formatCurrency}
            tooltipKey={item.key}
          />
        ))}
      </div>

      {/* ── Linha 2: Despesas + Gráficos ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '20px' }}>

        {/* Coluna Esquerda */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {[
              { title: 'Despesas Variáveis',                   key: 'despesas_variaveis' },
              { title: 'Despesas Fixas',                       key: 'despesas_fixas' },
              { title: 'Custos e Deduções',                    key: 'custos_e_deducoes' },
              { title: 'Impostos e Desp. não Operacionais',    key: 'impostos_desp_nao_operacionais' },
            ].map(item => (
              <DetailedMetricCard
                key={item.key}
                title={item.title}
                data={dashboardData[item.key]}
                formatCurrency={formatCurrency}
                tooltipKey={item.key}
              />
            ))}
          </div>
          <ChartCard
            title="Despesas por Categoria"
            data={despesasCategoriaChart}
            options={chartOptions('Despesas por Categoria')}
            height={220}
          />
        </div>

        {/* Coluna Direita */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <ChartCard
            title="Histórico de Faturamento Bruto"
            data={historicoFaturamentoChart}
            options={chartOptions('Histórico de Faturamento Bruto')}
            height={140}
          />
          <ChartCard
            title="Faturamento Diário (Mês)"
            data={faturamentoDiarioChart}
            options={chartOptions('Faturamento Diário')}
            height={140}
          />
          <ChartCard
            title="Faturamento por Serviços"
            data={faturamentoCategoriaChart}
            options={chartOptions('Faturamento por Serviços')}
            height={140}
          />
        </div>

      </div>
    </div>
  );
};

export default DashboardFinanceiroTab;
